import React, { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, Collapse, List, ListItem, Typography } from '@mui/material';
import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useStoreActions } from 'src/stores';
import { PopUpEnum } from 'src/stores/NotificationStore';
import {
  useGetLocaleCatalog,
  useGetLocaleTemplate,
  useGetLocaleTypes,
} from 'src/api/hooks/useLocaleTemplates';
import { Interweave } from 'interweave';
import { LocaleCatalogResponse, LocaleTemplateBody } from 'src/api/hooks/useLocaleTemplates.types';

const isEmailBody = (value: unknown): value is { subject?: unknown; html?: unknown } =>
  typeof value === 'object' && value !== null && 'html' in value;

const isHtmlDocument = (html: string): boolean => /<\s*html[\s>]/i.test(html);

const isHtmlFragment = (value: string): boolean => /<[a-z][\s\S]*>/i.test(value);

const HtmlPreview: React.FC<{ html: string }> = ({ html }) => {
  if (isHtmlDocument(html)) {
    return (
      <Box sx={{ bgcolor: 'common.white', borderRadius: 1, overflow: 'hidden' }}>
        <Box
          component="iframe"
          title="contract-preview"
          srcDoc={html}
          sx={{ width: '100%', height: '70vh', border: 0, bgcolor: 'common.white' }}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: 'common.white', color: 'grey.800', p: 2, borderRadius: 1 }}>
      <Interweave content={html} />
    </Box>
  );
};

const TemplateValue: React.FC<{ value: unknown }> = ({ value }) => {
  if (typeof value === 'string') {
    if (isHtmlFragment(value) || isHtmlDocument(value)) {
      return <HtmlPreview html={value} />;
    }

    return (
      <Typography component="pre" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', m: 0 }}>
        {value}
      </Typography>
    );
  }

  if (isEmailBody(value) && typeof value.html === 'string') {
    return (
      <Box>
        {typeof value.subject === 'string' && (
          <Typography sx={{ mb: 1 }}>{value.subject}</Typography>
        )}
        <HtmlPreview html={value.html} />
      </Box>
    );
  }

  return (
    <Typography component="pre" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', m: 0 }}>
      {JSON.stringify(value, null, 2)}
    </Typography>
  );
};

export const LocaleTemplates: React.FC = () => {
  const { t } = useTranslation();
  const setPopUpData = useStoreActions(action => action.popUp.setPopUp);

  const [types, setTypes] = useState<string[]>([]);
  const [selectedType, setSelectedType] = useState<string>();
  const [selectedGroup, setSelectedGroup] = useState<string>();
  const [catalog, setCatalog] = useState<LocaleCatalogResponse>();
  const [expandedPath, setExpandedPath] = useState<string>();
  const [template, setTemplate] = useState<LocaleTemplateBody>();

  const [getLocaleTypes, { loading: typesLoading }] = useGetLocaleTypes({
    onComplete: res => {
      if (res) {
        setTypes(res.keys);
      }
    },
    onError: () => {
      setPopUpData({
        isOpen: true,
        type: PopUpEnum.error,
        message: t('alerts.localeTemplates.error'),
      });
    },
  });

  const [getLocaleCatalog, { loading: catalogLoading }] = useGetLocaleCatalog({
    onComplete: res => {
      if (res) {
        setCatalog(res);
      }
    },
    onError: () => {
      setPopUpData({
        isOpen: true,
        type: PopUpEnum.error,
        message: t('alerts.localeTemplates.error'),
      });
    },
  });

  const [getLocaleTemplate, { loading: templateLoading }] = useGetLocaleTemplate({
    onComplete: res => {
      if (res) {
        setTemplate(res);
      }
    },
    onError: () => {
      setPopUpData({
        isOpen: true,
        type: PopUpEnum.error,
        message: t('alerts.localeTemplates.error'),
      });
    },
  });

  useEffect(() => {
    getLocaleTypes();
  }, []);

  const groupKeys = catalog ? Object.keys(catalog.groups) : [];
  const leafNames = selectedGroup ? catalog?.groups[selectedGroup] || [] : catalog?.leaves || [];

  const handleTypeClick = (type: string) => {
    setSelectedType(type);
    setSelectedGroup(undefined);
    setCatalog(undefined);
    setExpandedPath(undefined);
    setTemplate(undefined);
    getLocaleCatalog({ variables: { type } });
  };

  const handleGroupClick = (group: string) => {
    setSelectedGroup(group);
    setExpandedPath(undefined);
    setTemplate(undefined);
  };

  const handleLeafClick = (leafName: string) => {
    const path = selectedGroup ? `${selectedGroup}.${leafName}` : leafName;

    if (expandedPath === path) {
      setExpandedPath(undefined);
      setTemplate(undefined);
      return;
    }

    setExpandedPath(path);
    setTemplate(undefined);

    if (selectedType) {
      getLocaleTemplate({ variables: { type: selectedType, path } });
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {typesLoading && !types.length ? <CircularProgress size={24} /> : null}

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
        {types.map(type => (
          <Button
            key={type}
            variant={selectedType === type ? 'contained' : 'outlined'}
            color="primary"
            onClick={() => handleTypeClick(type)}>
            {type}
          </Button>
        ))}
      </Box>

      {catalogLoading ? <CircularProgress size={24} /> : null}

      {groupKeys.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          {groupKeys.map(group => (
            <Button
              key={group}
              variant={selectedGroup === group ? 'contained' : 'outlined'}
              color="primary"
              onClick={() => handleGroupClick(group)}>
              {group}
            </Button>
          ))}
        </Box>
      )}

      <List disablePadding>
        {leafNames.map(leafName => {
          const path = selectedGroup ? `${selectedGroup}.${leafName}` : leafName;
          const isExpanded = expandedPath === path;

          return (
            <Box
              key={path}
              sx={{
                mb: 1,
                borderRadius: 1,
                overflow: 'hidden',
                bgcolor: isExpanded ? 'action.selected' : 'secondary.main',
                borderLeft: 3,
                borderColor: isExpanded ? 'primary.main' : 'transparent',
              }}>
              <ListItem onClick={() => handleLeafClick(leafName)} sx={{ py: 1.5 }}>
                <Typography variant="h4" sx={{ flex: 1, fontWeight: 500 }}>
                  {leafName}
                </Typography>
                {isExpanded ? <ExpandLess /> : <ExpandMore />}
              </ListItem>
              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                <Box sx={{ px: 2, pb: 3, pt: 1 }}>
                  {templateLoading && !template ? <CircularProgress size={20} /> : null}
                  {template &&
                    Object.entries(template).map(([key, value]) => (
                      <Box key={key} sx={{ mb: 2 }}>
                        <Typography sx={{ mb: 1 }}>{key}:</Typography>
                        <TemplateValue value={value} />
                      </Box>
                    ))}
                </Box>
              </Collapse>
            </Box>
          );
        })}
      </List>
    </Box>
  );
};
